import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useStore } from "vuex";
import figureFormatter from "@/services/utils/figureFormatter";
import useDate from '@/services/utils/day';
import { isVisible, getActiveSetting, getPageMargins, resolveFontSize } from '@/services/utils/pdf/pdfCustomizationHelper';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}
const pdfPrinter = () => {
    const store = useStore();
    const {commaFormat, formatNameWithSerial} = figureFormatter();
    const { formatDate } = useDate();

    const userName = store.state.user.user.name;
    const defaultPageMargins = [10, 100, 10, 80];
    const defaultFontSize = 8;
    const pageWidth = 595.28;
    const defaultFooterNote = 'N.B: This is a system generated documents and requires no manual signature.';

    const exportToPDF = (company, incentivePaymentData, detailsReport = false, isBillCollection = false, customizationFields = {}) => {
        const pageMargins = getPageMargins(customizationFields, defaultPageMargins);

        var doc = {
            pageSize: 'A4',
            pageMargins: pageMargins,
            header: getHeader(company, customizationFields, pageMargins),
            footer: getFooter(userName, customizationFields, pageMargins),
            content: getContent(incentivePaymentData, detailsReport, isBillCollection, customizationFields),

            styles: {
                header: {
                    fontSize: 24,
                }
            },

            defaultStyle: {
                color: 'black',
                fontSize: resolveFontSize(customizationFields, defaultFontSize),
                font: 'SulaimanLipi'
            },

            info: {
                title: getTitle(detailsReport, isBillCollection),
            }
        }

        pushContentBody(doc, incentivePaymentData, detailsReport, isBillCollection)

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    function getTitle(detailsReport, isBillCollection) {
        if (detailsReport && isBillCollection) return "Bill Collection Detailed Statement";
        if (detailsReport && !isBillCollection) return "Incentive Detailed Statement";
        if (!detailsReport && isBillCollection) return "Bill Collection Summary Statement";
        return "Incentive Summary Statement";
    }

    const validValue = (value) => {
        if (! value) return '';
        if (value === 'null') return '';
        if (value === 'undefined') return '';
        return value;
    }

    const getHeader = (company, customizationFields, pageMargins) => {
        const coverPhoto = getActiveSetting(customizationFields, 'extra_settings_cover_photo_upload');

        if (coverPhoto && coverPhoto.value64) {
            return {
                columns: [
                    {
                        image: coverPhoto.value64,
                        width: pageWidth,
                        height: pageMargins[1] || defaultPageMargins[1],
                        alignment: 'center'
                    }
                ]
            }
        }

        return {
            margin: [ 25, 25, 25, 10 ],
            columns: [
                isVisible(customizationFields, 'heading_left_logo') ? {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                } : null,

                {
                    alignment: 'right',
                    stack: getCompanyLines(company, customizationFields)
                }
            ].filter(column => !! column)
        }
    }

    const getCompanyLines = (company, customizationFields) => {
        const address = validValue(company.address);
        const phone = validValue(company.phone);
        const email = validValue(company.email);
        const vatNo = validValue(company.vat_no);

        return [
            { key: 'heading_right_company_name', text: validValue(company.name) },
            { key: 'heading_right_company_address', text: address ? 'Address: ' + address : '' },
            { key: 'heading_right_company_phone', text: phone ? 'Phone: ' + phone : '' },
            { key: 'heading_right_company_email', text: email ? 'E-mail: ' + email : '' },
            { key: 'heading_right_vat_reg_number', text: vatNo ? 'VAT Reg No: ' + vatNo : '' },
        ].filter(line => isVisible(customizationFields, line.key)).map(line => line.text)
    }

    const getFooterNote = (customizationFields) => {
        const setting = getActiveSetting(customizationFields, 'extra_settings_footer_note');
        if (! setting) return defaultFooterNote;
        return setting.value || defaultFooterNote;
    }

    const getFooter = (userName, customizationFields, pageMargins) => {
        const footerPhoto = getActiveSetting(customizationFields, 'extra_settings_footer_photo_upload');

        if (footerPhoto && footerPhoto.value64) {
            return {
                columns: [
                    {
                        image: footerPhoto.value64,
                        width: pageWidth,
                        height: pageMargins[3] || defaultPageMargins[3],
                        alignment: 'center'
                    }
                ]
            }
        }

        return {
            margin: [ 25, 0 ],
            columns: [
                isVisible(customizationFields, 'footer_note') ? {
                    width: '*',
                    alignment: 'left',
                    text: getFooterNote(customizationFields),
                    margin: [5, 53, 0, 0],
                    fontSize: 8
                } : { width: '*', text: '' },

                isVisible(customizationFields, 'footer_printed_by') ? {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                } : null
            ].filter(column => !! column)
        }
    }

    const getContent = (incentivePaymentData, detailsReport, isBillCollection, customizationFields) => {
        return [
            ...getTitleNode(detailsReport, isBillCollection, customizationFields),

            {
                margin: [0, 20, 0, 0],
                alignment: 'justify',
                columns: [
                    {
                        width: '50%',
                        stack: getInfoLines(incentivePaymentData.data.details, customizationFields)
                    },

                ]
            },

            ...getMainTable(incentivePaymentData, detailsReport, isBillCollection, customizationFields),

            ...formatSignatureData(customizationFields)
        ]
    }

    const getTitleNode = (detailsReport, isBillCollection, customizationFields) => {
        if (! isVisible(customizationFields, 'body_top_left_report_title')) return [];

        return [
            {
                text: getTitle(detailsReport, isBillCollection),
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                fontSize: 12,
                decoration: 'underline'
            }
        ];
    }

    const getInfoLines = (details, customizationFields) => {
        return [
            { key: 'body_top_left_referrer_name', text: `Name: ${formatNameWithSerial(details.referrer_name, details.referrer_serial_no)}` },
            { key: 'body_top_left_payment_date', text: `Payment Date: ${details.payment_date}` },
        ].filter(line => isVisible(customizationFields, line.key)).map(line => ({ text: line.text, alignment: 'left', width: '50%' }))
    }

    const getTableNode = (widths, body) => {
        return {
            style: 'tableExample',
            margin: [0, 10, 0, 0],
            table: {
                body: body,
                widths: widths
            },
            layout: {
                fillColor: function (rowIndex, node, columnIndex) {
                    return (rowIndex === 0) ? '#f3f2f7' : null;
                },
                hLineWidth: function (i, node) {
                    return 0.5;
                },
                vLineWidth: function (i, node) {
                    return 0.5;
                }
            }
        };
    }

    const getMainTable = (incentivePaymentData, detailsReport, isBillCollection, customizationFields) => {
        if (isBillCollection) return [getTableNode(customWidth(isBillCollection), [])];

        const columns = getIncentiveColumns(customizationFields);
        if (! columns.length) return [];

        return [getTableNode(columns.map(() => '*'), getIncentiveTableBody(columns, incentivePaymentData, detailsReport, customizationFields))];
    }

    const getIncentiveColumns = (customizationFields) => {
        return [
            { key: 'body_table_invoice_date', header: 'Invoice Date', cell: row => row.sale_master.date },
            { key: 'body_table_invoice_no', header: 'Invoice No', cell: row => row.sale_master.bill_number },
            { key: 'body_table_patient_name', header: 'Patient Name (ID)', cell: row => row.sale_master.patient_name + '(' + row.sale_master.patient_serial_no + ')' },
            { key: 'body_table_total_bill', header: 'Total Bill', field: 'total_invoice' },
            { key: 'body_table_total_discount', header: 'Total Discount', field: 'total_discount' },
            { key: 'body_table_net_bill', header: 'Net Bill', field: 'net_bill' },
            { key: 'body_table_total_incentive', header: 'Total Incentive', field: 'total_incentive' },
            { key: 'body_table_referrer_discount', header: 'Referrer Discount', field: 'referrer_discount' },
            { key: 'body_table_net_incentive', header: 'Net Incentive', field: 'net_incentive' },
        ].filter(column => isVisible(customizationFields, column.key))
    }

    const toCell = (column, text) => column.field ? { text: text, alignment: 'right' } : { text: text };

    const getCellText = (column, row) => column.field ? commaFormat(row[column.field]) : column.cell(row);

    const getIncentiveTableBody = (columns, incentivePaymentData, detailsReport, customizationFields) => {
        const header = columns.map(column => toCell(column, column.header));
        const body = incentivePaymentData.data.data.flatMap(row => [
            columns.map(column => toCell(column, getCellText(column, row))),
            ...getItemDetailsRow(columns, row, detailsReport, customizationFields)
        ]);
        if (! isVisible(customizationFields, 'body_totals_table_total_row')) return [header, ...body];
        if (! columns.some(column => column.field)) return [header, ...body];
        return [header, ...body, getIncentiveTotalRow(columns, incentivePaymentData.data.details)];
    }

    const getItemDetailsRow = (columns, row, detailsReport, customizationFields) => {
        if (! detailsReport) return [];
        if (! isVisible(customizationFields, 'body_table_item_details')) return [];

        return [[{ text: row.item_details || '', colSpan: columns.length }, ...Array(columns.length - 1).fill({})]];
    }

    const getIncentiveTotalRow = (columns, details) => {
        const labelSpan = columns.findIndex(column => column.field);
        const totalCells = columns.slice(labelSpan).map(column => ({ text: details[column.field], alignment: 'right' }));
        if (labelSpan === 0) return totalCells;
        return [{ text: 'Total', alignment: 'right', colSpan: labelSpan }, ...Array(labelSpan - 1).fill({}), ...totalCells];
    }

    const pushContentBody = (doc, incentivePaymentData, detailsReport, isBillCollection) => {
        if (! isBillCollection) return;

        pushIntoTable(doc, incentivePaymentData, detailsReport, isBillCollection);
    }

    const customWidth = ( isBillCollection) => {
        if (isBillCollection){
            return [ '17%', '18%','20%', '15%', '15%', '15%'];
        }
        return [ '12%', '13%','15%', '10%', '10%', '10%', '10%', '10%', '10%' ]
    }

    const pushIntoTable = (doc, incentivePaymentData, detailsReport, isBillCollection) => {
        const tableItems = incentivePaymentData.data.data;
        const tableHeader = getTableHeader(isBillCollection);
        doc.content[2].table.body.push(tableHeader);

        tableItems.forEach((item, i) => {
            const rowData = getRowData(item, i, isBillCollection);
            doc.content[2].table.body.push(rowData);

            if (!detailsReport){
                return
            }

            const detailsRow = getRowDetails(item.item_details || '', isBillCollection);
            doc.content[2].table.body.push(detailsRow);
        });

        const getLastRow = getTotal(incentivePaymentData.data.details, isBillCollection)
        doc.content[2].table.body.push(getLastRow);
    }

    const getTableHeader = (isBillCollection) => {
        const tableHeader = [
            {text: 'Invoice Date'},
            {text: 'Invoice No'},
            {text: 'Patient Name (ID)'},
            {text: 'Total Bill', alignment: 'right'},
            {text: 'Total Discount', alignment: 'right'},
            {text: 'Net Bill', alignment: 'right'},
        ]

        if (isBillCollection){
            return tableHeader
        }

        tableHeader.push(
            {text: 'Total Incentive', alignment: 'right'},
            {text: 'Referrer Discount', alignment: 'right'},
            {text: 'Net Incentive', alignment: 'right'},
        )

        return tableHeader
    }

    const getRowData = (item, i, isBillCollection) => {

        if (isBillCollection){
            return [
                {text: item.sale_master_date},
                {text: item.bill_number},
                {text: item.contact_profile_name + '(' + item.cp_serial_no + ')' },
                {text: commaFormat(item.total_invoice), alignment: 'right'},
                {text: commaFormat(item.total_discount), alignment: 'right'},
                {text: commaFormat(item.net_bill), alignment: 'right'},
            ]
        }

        const tableRow = [
            {text: item.sale_master.date},
            {text: item.sale_master.bill_number},
            {text: item.sale_master.patient_name + '(' + item.sale_master.patient_serial_no + ')' },
            {text: commaFormat(item.total_invoice), alignment: 'right'},
            {text: commaFormat(item.total_discount), alignment: 'right'},
            {text: commaFormat(item.net_bill), alignment: 'right'},
            {text: commaFormat(item.total_incentive), alignment: 'right'},
            {text: commaFormat(item.referrer_discount), alignment: 'right'},
            {text: commaFormat(item.net_incentive), alignment: 'right'},
     ]

        return tableRow
    }

    const formatSignatureData = (customizationFields) => {
        const signatures = [
            { key: 'signature_prepared_by', title: 'Prepared By' },
            { key: 'signature_reviewed_by', title: 'Reviewed By' },
            { key: 'signature_approved_by', title: 'Approved By' },
        ].filter(signature => isVisible(customizationFields, signature.key)).map(signature => signature.title);
        if (! signatures.length) return [];

        return [
            {
                alignment: 'justify',
                absolutePosition: { x: 60, y: 740 },
                columns: signatures.map(getSingleSignature)
            }
        ];
    }

    const getSingleSignature = (title) => {
        return {
            width: '43%',
            stack: [
                {
                    canvas: [
                        {
                            type: 'line',
                            x1: 0,
                            y1: 0,
                            x2: 58,
                            y2: 0,
                            lineWidth: 1
                        }
                    ]
                },
                {
                    margin: [ 0, 5, 0, 0 ],
                    text: `${title} `,
                    color: 'black',
                    bold: true,
                    alignment: 'left',
                }
            ]
        }
    }

    const getRowDetails = (itemDetails, isBillCollection) => {
        return [
            {
                text: itemDetails,
                colSpan: isBillCollection ? 6 : 9
            },
            isBillCollection ? [{}, {}, {}, {}, {}] : [{}, {}, {}, {}, {}, {}, {}, {}]
        ];
    };

    const getTotal = (totalValue, isBillCollection) => {
        let colSpanValue = 3;
        let emptyCells = 2;

        const lastRow = [
            {
                text: 'Total',
                alignment: 'right',
                colSpan: colSpanValue
            },

            ...Array(emptyCells).fill({}),

            {
                text: totalValue.total_invoice,
                alignment: 'right'
            },

            {
                text: totalValue.total_discount,
                alignment: 'right'
            },

            {
                text: totalValue.net_bill,
                alignment: 'right'
            },
        ]

        if (isBillCollection){
            return lastRow;
        }

        lastRow.push(
            {
                text: totalValue.total_incentive,
                alignment: 'right'
            },

            {
                text: totalValue.referrer_discount,
                alignment: 'right'
            },

            {
                text: totalValue.net_incentive,
                alignment: 'right'
            }
         )

        return lastRow
    }

    return {
        exportToPDF
    }

}

export default pdfPrinter;